import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../../utils/apiClient';

const MatBorePercentagesModal = React.memo(({ isOpen, onClose, projectId, jobNumber, onSaveSuccess, onSaveError }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [percentages, setPercentages] = useState({
    sb_cs: '',
    lb_cs: '',
    sb_ss: '',
    lb_ss: '',
    lb_dl: '',
    sb_dl: '',
    na: ''
  });
  const [hasExistingData, setHasExistingData] = useState(false);

  // Fetch existing percentages when modal opens
  useEffect(() => {
    if (isOpen && jobNumber) {
      fetchPercentages();
    }
  }, [isOpen, jobNumber]);

  const fetchPercentages = async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/mat-bore-percentages/${jobNumber}`
      );

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setPercentages({
          sb_cs: data.sb_cs || '',
          lb_cs: data.lb_cs || '',
          sb_ss: data.sb_ss || '',
          lb_ss: data.lb_ss || '',
          lb_dl: data.lb_dl || '',
          sb_dl: data.sb_dl || '',
          na: data.na || ''
        });
        setHasExistingData(true);
      } else {
        // No existing data, start with empty values
        setPercentages({
          sb_cs: '',
          lb_cs: '',
          sb_ss: '',
          lb_ss: '',
          lb_dl: '',
          sb_dl: '',
          na: ''
        });
        setHasExistingData(false);
      }
    } catch (error) {
      console.error('Error fetching mat bore percentages:', error);
      // Start with empty if fetch fails
      setPercentages({
        sb_cs: '',
        lb_cs: '',
        sb_ss: '',
        lb_ss: '',
        lb_dl: '',
        sb_dl: '',
        na: ''
      });
      setHasExistingData(false);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    // Allow empty string or valid decimal numbers
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setPercentages(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Convert empty strings to null and strings to numbers
      const dataToSave = {
        job_number: jobNumber,
        sb_cs: percentages.sb_cs === '' ? null : parseFloat(percentages.sb_cs),
        lb_cs: percentages.lb_cs === '' ? null : parseFloat(percentages.lb_cs),
        sb_ss: percentages.sb_ss === '' ? null : parseFloat(percentages.sb_ss),
        lb_ss: percentages.lb_ss === '' ? null : parseFloat(percentages.lb_ss),
        lb_dl: percentages.lb_dl === '' ? null : parseFloat(percentages.lb_dl),
        sb_dl: percentages.sb_dl === '' ? null : parseFloat(percentages.sb_dl),
        na: percentages.na === '' ? null : parseFloat(percentages.na)
      };

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/mat-bore-percentages',
        dataToSave
      );

      if (response.data.success) {
        onSaveSuccess('Material bore percentages saved successfully!');
        onClose();
      } else {
        throw new Error(response.data.message || 'Failed to save percentages');
      }
    } catch (error) {
      console.error('Error saving mat bore percentages:', error);
      onSaveError(error.response?.data?.message || error.message || 'Failed to save percentages');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            Material Bore Percentages for Prep & Material Handling
          </h3>
          <button
            onClick={handleClose}
            disabled={saving}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: saving ? 'not-allowed' : 'pointer',
              color: '#666',
              padding: '0.25rem',
              opacity: saving ? 0.5 : 1
            }}
          >
            ×
          </button>
        </div>

        <div style={{
          backgroundColor: '#e3f2fd',
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1.5rem',
          fontSize: '0.9rem',
          color: '#1976d2'
        }}>
          <strong>Job Number:</strong> {jobNumber || 'N/A'}
          <br />
          <span style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
            These percentages represent 0.3% of material bore for prep and material handling.
          </span>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #007bff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ marginTop: '1rem', color: '#666' }}>Loading percentages...</p>
          </div>
        ) : (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              {/* SB CS */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: '0.9rem'
                }}>
                  SB CS (Small Bore Carbon Steel)
                </label>
                <input
                  type="text"
                  value={percentages.sb_cs}
                  onChange={(e) => handleChange('sb_cs', e.target.value)}
                  placeholder="0.00"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* LB CS */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: '0.9rem'
                }}>
                  LB CS (Large Bore Carbon Steel)
                </label>
                <input
                  type="text"
                  value={percentages.lb_cs}
                  onChange={(e) => handleChange('lb_cs', e.target.value)}
                  placeholder="0.00"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* SB SS */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: '0.9rem'
                }}>
                  SB SS (Small Bore Stainless Steel)
                </label>
                <input
                  type="text"
                  value={percentages.sb_ss}
                  onChange={(e) => handleChange('sb_ss', e.target.value)}
                  placeholder="0.00"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* LB SS */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: '0.9rem'
                }}>
                  LB SS (Large Bore Stainless Steel)
                </label>
                <input
                  type="text"
                  value={percentages.lb_ss}
                  onChange={(e) => handleChange('lb_ss', e.target.value)}
                  placeholder="0.00"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* LB DL */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: '0.9rem'
                }}>
                  LB DL (Large Bore Dual)
                </label>
                <input
                  type="text"
                  value={percentages.lb_dl}
                  onChange={(e) => handleChange('lb_dl', e.target.value)}
                  placeholder="0.00"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* SB DL */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: '0.9rem'
                }}>
                  SB DL (Small Bore Dual)
                </label>
                <input
                  type="text"
                  value={percentages.sb_dl}
                  onChange={(e) => handleChange('sb_dl', e.target.value)}
                  placeholder="0.00"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* NA */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: '0.9rem'
                }}>
                  NA (Not Applicable)
                </label>
                <input
                  type="text"
                  value={percentages.na}
                  onChange={(e) => handleChange('na', e.target.value)}
                  placeholder="0.00"
                  disabled={saving}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleClose}
                disabled={saving}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  opacity: saving ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: saving ? '#6c757d' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {saving && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #fff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                )}
                {saving ? 'Saving...' : 'Save Percentages'}
              </button>
            </div>
          </>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
});

export default MatBorePercentagesModal;



